import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Dna, Sparkles, Trophy, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLottery } from "@/hooks/use-lottery-selection";
import { PlanGate } from "@/components/app/PlanGate";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface HistogramRow {
  hits: number;
  count: number;
}
interface PrizeRow {
  contest_number: number;
  draw_date: string | null;
  hits: number;
  tier: number;
}
interface DnaResult {
  lottery_slug: string;
  numbers_picked: number[];
  numbers_drawn: number;
  total_draws: number;
  histogram: HistogramRow[];
  prizes: PrizeRow[];
  is_dupla_sena?: boolean;
  sorteios?: Array<{
    sorteio: number;
    label: string;
    histogram: HistogramRow[];
    prizes: PrizeRow[];
    total_draws: number;
  }>;
}

// log-binomial via lgamma
function lnGamma(x: number) {
  const c = [
    76.18009172947146, -86.50532032941677, 24.01409824083091,
    -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5,
  ];
  let y = x;
  let t = x + 5.5;
  t -= (x + 0.5) * Math.log(t);
  let s = 1.000000000190015;
  for (let j = 0; j < 6; j++) s += c[j] / ++y;
  return -t + Math.log((2.5066282746310005 * s) / x);
}
function lnChoose(n: number, k: number) {
  if (k < 0 || k > n) return -Infinity;
  return lnGamma(n + 1) - lnGamma(k + 1) - lnGamma(n - k + 1);
}
/** P(X = k) for hypergeometric: pop=N, success=K (numbers drawn), draws=n (numbers picked) */
function hypergeomPMF(N: number, K: number, n: number, k: number) {
  return Math.exp(lnChoose(K, k) + lnChoose(N - K, n - k) - lnChoose(N, n));
}

const DNAContent = () => {
  const { config, selected } = useLottery();
  const location = useLocation();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [result, setResult] = useState<DnaResult | null>(null);
  const [cameFrom, setCameFrom] = useState<{ path: string; label: string } | null>(null);
  const [activeSorteio, setActiveSorteio] = useState<number>(1);

  const parsed = useMemo(() => {
    const nums = input
      .split(/[\s,;.\-\n]+/)
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n));
    const unique = Array.from(new Set(nums));
    const valid = unique.every((n) => n >= config.min && n <= config.max);
    return { nums: unique.sort((a, b) => a - b), valid };
  }, [input, config.min, config.max]);

  const canSubmit = parsed.nums.length === config.pick && parsed.valid;

  const mutation = useMutation({
    mutationFn: async (numbers: number[]) => {
      const { data, error } = await supabase.rpc("compute_dna", {
        _lottery_slug: selected,
        _numbers: numbers,
      });
      if (error) throw error;
      return data as unknown as DnaResult;
    },
    onSuccess: (data) => { setResult(data); setActiveSorteio(1); },
    onError: (err: Error) =>
      toast({ title: "Erro ao calcular", description: err.message, variant: "destructive" }),
  });

  // Pré-preencher e disparar análise quando vindo de outro módulo via navigate state
  const consumedStateRef = useRef(false);
  useEffect(() => {
    const state = location.state as
      | { numbers?: number[]; autoAnalyze?: boolean; from?: string; fromLabel?: string }
      | null;
    if (consumedStateRef.current) return;
    if (!state?.numbers?.length) return;
    consumedStateRef.current = true;
    const sorted = [...new Set(state.numbers)].sort((a, b) => a - b);
    const valid = sorted.every((n) => n >= config.min && n <= config.max);
    setInput(sorted.map((n) => String(n).padStart(2, "0")).join(" "));
    if (state.from) {
      setCameFrom({ path: state.from, label: state.fromLabel || "Voltar" });
    }
    // limpa o state para não re-disparar em refresh
    navigate(location.pathname, { replace: true });
    if (valid && sorted.length === config.pick && state.autoAnalyze) {
      // pequeno delay para garantir que o input foi pintado
      setTimeout(() => mutation.mutate(sorted), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnalyze = () => {
    if (parsed.nums.length === 0) {
      toast({
        title: "Digite seu jogo",
        description: `Cole as ${config.pick} dezenas separadas por espaço, vírgula ou hífen.`,
      });
      return;
    }
    if (!parsed.valid) {
      toast({
        title: "Dezenas fora do intervalo",
        description: `Use apenas números entre ${config.min} e ${config.max}.`,
        variant: "destructive",
      });
      return;
    }
    if (parsed.nums.length !== config.pick) {
      toast({
        title: `Faltam ${config.pick - parsed.nums.length} dezenas`,
        description: `Você inseriu ${parsed.nums.length} de ${config.pick} dezenas únicas.`,
        variant: "destructive",
      });
      return;
    }
    mutation.mutate(parsed.nums);
  };

  const reset = () => {
    setInput("");
    setResult(null);
  };

  // Active dataset (sorteio 1 ou 2 para Dupla Sena)
  const active = useMemo(() => {
    if (!result) return null;
    if (result.is_dupla_sena && result.sorteios?.length) {
      const s = result.sorteios.find((x) => x.sorteio === activeSorteio) ?? result.sorteios[0];
      return { histogram: s.histogram, prizes: s.prizes, total_draws: s.total_draws };
    }
    return { histogram: result.histogram, prizes: result.prizes, total_draws: result.total_draws };
  }, [result, activeSorteio]);

  // Expected histogram for a uniformly random game (same picks)
  const expected = useMemo(() => {
    if (!result || !active) return null;
    const N = config.max - config.min + 1;
    const K = result.numbers_drawn;
    const n = result.numbers_picked.length;
    return active.histogram.map((row) => ({
      hits: row.hits,
      expected: hypergeomPMF(N, K, n, row.hits) * active.total_draws,
    }));
  }, [result, active, config.max, config.min]);

  const chartData = useMemo(() => {
    if (!active) return [];
    return active.histogram.map((row, i) => ({
      hits: row.hits,
      acertos: row.count,
      esperado: expected ? Number(expected[i].expected.toFixed(2)) : 0,
      isPrize: row.hits >= (config.pick === 15 ? 11 : config.pick === 5 ? 2 : config.pick - 2),
    }));
  }, [active, expected, config.pick]);

  const totalPrizes = active?.prizes.length ?? 0;
  const topPrize = useMemo(() => {
    if (!active?.prizes.length) return null;
    return active.prizes.reduce((a, b) => (b.tier > a.tier ? b : a));
  }, [active]);

  return (
    <div className="space-y-6">
      {cameFrom && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(cameFrom.path)}
          className="-ml-2 h-8 gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {cameFrom.label}
        </Button>
      )}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.14em] text-primary">
          <Sparkles className="h-3 w-3" />
          Recurso Pro
        </div>
        <h1 className="font-serif text-3xl font-light text-foreground">
          DNA do <em className="not-italic text-primary">jogo</em>
        </h1>
        <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Cole um jogo de {config.name} e descubra quantas vezes ele teria acertado cada faixa de
          dezenas em todos os concursos já realizados.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Seu jogo · {config.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="dna-input" className="text-xs uppercase tracking-wider">
              {config.pick} dezenas (separe por espaço, vírgula ou hífen)
            </Label>
            <Input
              id="dna-input"
              placeholder={
                selected === "lotofacil"
                  ? "01 03 05 07 09 11 13 15 17 19 21 23 25 02 04"
                  : selected === "quina"
                  ? "07 23 35 47 62"
                  : "04 17 23 36 42 58"
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="font-mono text-base tracking-wider"
            />
            <div className="flex items-center justify-between text-xs">
              <span
                className={cn(
                  "text-muted-foreground",
                  parsed.nums.length > 0 && !parsed.valid && "text-destructive",
                  canSubmit && "text-lt-teal"
                )}
              >
                {parsed.nums.length} de {config.pick} dezenas
                {!parsed.valid && parsed.nums.length > 0 && ` · fora do intervalo ${config.min}–${config.max}`}
              </span>
              {parsed.nums.length > 0 && (
                <span className="font-mono text-muted-foreground">
                  {parsed.nums.map((n) => String(n).padStart(2, "0")).join(" · ")}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleAnalyze}
              disabled={mutation.isPending}
              variant={canSubmit ? "default" : "secondary"}
            >
              <Dna className="mr-2 h-4 w-4" />
              {mutation.isPending ? "Analisando histórico…" : "Analisar DNA"}
            </Button>
            {(input || result) && (
              <Button variant="ghost" onClick={reset}>
                Limpar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {mutation.isPending && (
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      )}

      {result && (
        <>
          {result.is_dupla_sena && result.sorteios && result.sorteios.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              {result.sorteios.map((s) => (
                <button
                  key={s.sorteio}
                  onClick={() => setActiveSorteio(s.sorteio)}
                  className={cn(
                    "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                    activeSorteio === s.sorteio
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-lt-border bg-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
          {/* Summary cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardContent className="pt-6">
                <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Concursos analisados
                </div>
                <div className="mt-1 font-serif text-3xl font-light text-foreground">
                  {(active?.total_draws ?? result.total_draws).toLocaleString("pt-BR")}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Vezes premiado
                </div>
                <div className="mt-1 font-serif text-3xl font-light text-primary">
                  {totalPrizes.toLocaleString("pt-BR")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {(active?.total_draws ?? 0) > 0
                    ? `${((totalPrizes / (active!.total_draws)) * 100).toFixed(2)}% dos concursos`
                    : "—"}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Maior acerto
                </div>
                <div className="mt-1 font-serif text-3xl font-light text-lt-teal">
                  {topPrize ? `${topPrize.hits} dezenas` : "—"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {topPrize ? `Concurso ${topPrize.contest_number}` : "Nenhum prêmio"}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Histogram */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Histograma de acertos</CardTitle>
              <p className="text-xs text-muted-foreground">
                Quantas vezes esse jogo teria acertado cada quantidade de dezenas. A linha
                tracejada mostra o esperado para um jogo aleatório.
              </p>
            </CardHeader>
            <CardContent>
            <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
              <div className="h-[320px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--lt-border))" />
                    <XAxis
                      dataKey="hits"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={11}
                      label={{ value: "Acertos", position: "insideBottom", offset: -4, fontSize: 11 }}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--lt-bg2))",
                        border: "1px solid hsl(var(--lt-border))",
                        borderRadius: 8,
                        fontSize: 12,
                        color: "hsl(var(--foreground))",
                      }}
                      itemStyle={{ color: "hsl(var(--foreground))" }}
                      labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 500 }}
                      cursor={{ fill: "hsl(var(--foreground) / 0.08)" }}
                      formatter={(value: number, name: string) => [
                        value.toLocaleString("pt-BR"),
                        name === "acertos" ? "Seu jogo" : "Esperado (aleatório)",
                      ]}
                      labelFormatter={(l) => `${l} acerto${l === 1 ? "" : "s"}`}
                    />
                    <Bar dataKey="acertos" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={entry.isPrize ? "hsl(var(--primary))" : "hsl(var(--lt-cream3))"}
                        />
                      ))}
                    </Bar>
                    <Bar
                      dataKey="esperado"
                      fill="hsl(var(--lt-teal))"
                      fillOpacity={0.25}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="overflow-hidden rounded-lg border border-lt-border">
                <Table>
                  <TableHeader>
                    <TableRow className="border-lt-border">
                      <TableHead className="text-xs uppercase tracking-wider">Acertos</TableHead>
                      <TableHead className="text-right text-xs uppercase tracking-wider">Quantidade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(active?.histogram ?? []).map((row) => (
                      <TableRow key={row.hits} className="border-lt-border">
                        <TableCell className="font-mono tabular-nums">{row.hits}</TableCell>
                        <TableCell className="text-right font-mono font-medium tabular-nums">
                          {row.count.toLocaleString("pt-BR")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            </CardContent>
          </Card>

          {/* Prizes list */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Trophy className="h-4 w-4 text-primary" />
                Concursos em que você teria sido premiado
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(active?.prizes.length ?? 0) === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Esse jogo nunca teria sido premiado em {(active?.total_draws ?? 0).toLocaleString("pt-BR")}{" "}
                  concursos.
                </p>
              ) : (
                <div className="max-h-[400px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Concurso</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead className="text-right">Acertos</TableHead>
                        <TableHead className="text-right">Faixa</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(active?.prizes ?? []).map((p) => (
                        <TableRow key={p.contest_number}>
                          <TableCell className="font-mono">{p.contest_number}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {p.draw_date
                              ? new Date(p.draw_date).toLocaleDateString("pt-BR")
                              : "—"}
                          </TableCell>
                          <TableCell className="text-right font-medium">{p.hits}</TableCell>
                          <TableCell className="text-right">
                            <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                              {p.tier} acertos
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const DNA = () => (
  <PlanGate
    tier="pro"
    featureName="DNA do jogo"
    description="Compare seu jogo contra todo o histórico de concursos e descubra quantas vezes ele teria acertado cada faixa de dezenas. Disponível no plano Pro."
  >
    <DNAContent />
  </PlanGate>
);

export default DNA;